/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import { string, number, bool, array } from 'prop-types';
import classNames from 'clsx';

import NotificationSymbol from 'components/NotificationSymbol/NotificationSymbol';
import Avatar from 'components/Avatar/Avatar';

import styles from './job-card.scss';

import Button from 'components/Button/Button';

/**
 * This is the JobCard component class.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */

const RequestedMember = props => {
  const { fullname } = props;
  const [firstName] = fullname.split(' ');

  return (
    <div className={styles.requestedMember}>
      <Avatar>{fullname}</Avatar>
      <p>{firstName}</p>
    </div>
  );
};

RequestedMember.propTypes = {
  fullname: string.isRequired,
  avatar_url_small: string,
};

RequestedMember.defaultProps = {
  avatar_url_small: undefined,
};

const JobCard = ({
  job_id,
  title,
  count_requested,
  count_confirmed,
  has_new_content,
  talents,
  date,
  className: customClassName,
}) => {
  return (
    <article
      className={classNames(styles.wrapper, customClassName, {
        [styles.new]: has_new_content,
      })}
    >
      <p className={classNames(styles.date, styles.truncated)}>{date}</p>
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.invitesWrapper}>
        <div className={styles.requestedMembers}>
          {talents.map(({ talent_id, ...talent }, index) => {
            // max three images
            if (index < 3) {
              return <RequestedMember key={talent_id} {...talent} />;
            }
          })}
          {talents.length > 3 && (
            <span
              className={classNames(
                styles.requestedMemberOverflow,
                styles.truncated
              )}
            >
              +{talents.length - 3}
            </span>
          )}
        </div>

        <div className={styles.invitesContainer}>
          <div className={styles.inviteBox}>
            <p className={classNames(styles.subtitle, styles.truncated)}>
              Confirmed
            </p>
            <p className={classNames(styles.count, styles.truncated)}>
              {count_confirmed}
            </p>
          </div>
          <div className={styles.inviteBox}>
            {has_new_content && <NotificationSymbol inline />}
            <p className={classNames(styles.subtitle, styles.truncated)}>
              Requested
            </p>
            <p className={classNames(styles.count, styles.truncated)}>
              {count_requested}
            </p>
          </div>
        </div>
      </div>
      <div className={styles.divider}></div>
      <Button
        path={`/account/jobs/view/${job_id}`}
        grey
        className={styles.button}
      >
        View project
      </Button>
    </article>
  );
};

/**
 * Declare expected property types.
 *
 * @type {Object}
 */
JobCard.propTypes = {
  job_id: string.isRequired,
  title: string.isRequired,
  talents: array.isRequired,
  count_requests: number,
  count_requested: number,
  count_confirmed: number,
  has_new_content: bool,
  location: string,
  date: string,
  className: string,
};

/**
 * Set default properties.
 *
 * @type {Object}
 */
JobCard.defaultProps = {
  count_requests: 0,
  count_confirmed: 0,
  has_new_content: false,
  location: '',
  date: '',
  className: null,
};

export default JobCard;
